import React from "react";
import { FooterLinks, SocialLinks } from "./constants";
import { HashLink } from "react-router-hash-link";
import logo from "./assets/m2_gas_logo_white.png";
import { Phone, Mail, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-[#262626] to-[#1a1a1a] text-white px-6 sm:px-12 md:px-20 lg:px-32 py-16 border-t border-slate-700/50">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info & Logo */}
          <div className="lg:col-span-1">
            <img src={logo} alt="M2 Gas Services" className="w-48 mb-6" />
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Professional gas line services serving Mooresville and surrounding areas since 2009. 
              Licensed, insured, and committed to your safety.
            </p>
            <div className="flex gap-3">
              {SocialLinks.map((item, index) => (
                <a
                  key={index}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.name}
                  className="bg-slate-700/50 p-3 rounded-full hover:bg-[#2DA8FF] transition-all duration-300 hover:scale-110 group"
                >
                  <div className="text-slate-300 group-hover:text-white transition-colors">
                    {item.icon}
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-bold mb-5 flex items-center gap-2">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {FooterLinks[0]?.links.map((link, j) => (
                <li key={j}>
                  <HashLink
                    smooth
                    to={link.path}
                    className="text-slate-400 hover:text-[#2DA8FF] transition-all duration-200 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-[#2DA8FF] group-hover:w-3 transition-all duration-200"></span>
                    {link.name}
                  </HashLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white text-lg font-bold mb-5">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:9805406064"
                  className="flex items-start gap-3 text-slate-400 hover:text-[#2DA8FF] transition-colors group"
                >
                  <Phone className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#2DA8FF]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Phone</div>
                    <div className="text-sm group-hover:underline">(980) 540-6064</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@m2gasservices.com"
                  className="flex items-start gap-3 text-slate-400 hover:text-[#2DA8FF] transition-colors group cursor-pointer"
                >
                  <Mail className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#2DA8FF]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Email</div>
                    <div className="text-sm group-hover:underline break-words">
                      support@m2gasservices.com
                    </div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=133+Hightower+Ct+Mooresville+NC+28117"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 text-slate-400 hover:text-[#2DA8FF] transition-colors group"
                >
                  <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#2DA8FF]" />
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Address</div>
                    <div className="text-sm group-hover:underline">
                      133 Hightower Ct<br />
                      Mooresville, NC 28117
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="text-white text-lg font-bold mb-5">
              Business Hours
            </h3>
            <ul className="space-y-3">
              <li className="flex justify-between gap-4 text-sm">
                <span className="text-slate-300 font-medium">Monday - Friday</span>
                <span className="text-slate-400">8:00 AM - 5:00 PM</span>
              </li>
              <li className="flex justify-between gap-4 text-sm">
                <span className="text-slate-300 font-medium">Saturday</span>
                <span className="text-slate-400">9:00 AM - 2:00 PM</span>
              </li>
              <li className="flex justify-between gap-4 text-sm">
                <span className="text-slate-300 font-medium">Sunday</span>
                <span className="text-slate-400">Closed</span>
              </li>
            </ul>
            
            {/* CTA in footer */}
            <div className="mt-8 p-4 bg-gradient-to-br from-orange-600/20 to-orange-800/20 border border-orange-500/30 rounded-lg">
              <p className="text-xs text-orange-300 mb-3">Need service now?</p>
              <a
                href="tel:9805406064"
                className="inline-flex items-center justify-center gap-2 w-full bg-[#2DA8FF] hover:bg-[#2DA8FF]/90 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-200 text-sm hover:scale-105"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-700/50 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} M2 Gas Services. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <a href="/privacy-policy" className="hover:text-[#2DA8FF] transition-colors">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="hover:text-[#2DA8FF] transition-colors">
                Terms of Service
              </a>
              <span className="text-slate-600">
                Licensed & Insured
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;